import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  Menu, X, Search, Bell, MessageSquare, HelpCircle, Download 
} from "lucide-react";
import Credit from "../pages/Parts/Credit";
import User from "../pages/Parts/User";
import ExploreModal from "./ExploreModal";
import Login from "../pages/Auth/Login";
import Register from "../pages/Auth/Register";
import { API, decode, Event } from "utils/Helper";
import storage from "utils/Storage";
import axios from "axios";
import Cookies from "js-cookie";
import { formatUserDateTime } from "utils/timezoneHelper";

export function Header({ 
  isCollapsedMobile, 
  setIsCollapsedMobile, 
  isAuthed, 
  onChatToggle, 
  isCollapsed, 
  setIsCollapsed 
}) {
  const [notifications, setNotifications] = useState([]);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [siteConfig, setSiteConfig] = useState(null);
  
  const location = useLocation();
  const path = location.pathname;
  const isCasinoActive = path.startsWith('/casino') || path === '/' || path.startsWith('/group');
  const isSportsActive = path.startsWith('/sports');
  const showFullLogo = !isCollapsed;

  const notifRef = React.useRef(null);


  // Fetch notifications
  const getNotifications = async () => {
  try {
    const uid = Cookies.get("uid") || storage.getKey("id");
    if (!uid) return;
    const res = await axios.get(`${API}/api/notifications/${uid}`);
    if (res.data && res.data.status) {
      setNotifications(res.data.data || []);
    }
  } catch (e) {
    console.error("Notifications fetch error:", e);
  }
};

useEffect(() => {
  if (isAuthed) {
    getNotifications();
    const interval = setInterval(getNotifications, 20000);
    return () => clearInterval(interval);
  }
}, [isAuthed]);

// Open modals if direct route is /login or /register
const routerLocation = useLocation();
useEffect(() => {
  if (routerLocation.pathname === "/login") {
    Event.emit("login_form");
  } else if (routerLocation.pathname === "/register") {
    Event.emit("register_form");
  }
}, [routerLocation.pathname]);

// Fetch site config
useEffect(() => {
  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/site-settings`);
      if (res.data && res.data.status === 200) {
        const cfg = res.data.data?.[0] || {};
        setSiteConfig(cfg);
        // Make config globally accessible for all components
        window.siteConfig = { ...window.siteConfig, ...cfg };
      }
    } catch (e) {
      console.error("Config fetch error:", e);
    }
  };
  fetchConfig();
}, []);

// Handle outside click for notifications
useEffect(() => {
  const handleOutsideClick = (e) => {
    if (notifRef.current && !notifRef.current.contains(e.target)) {
      setIsNotifOpen(false);
    }
  };
  document.addEventListener("mousedown", handleOutsideClick);
  return () => document.removeEventListener("mousedown", handleOutsideClick);
}, []);

const handleClearNotification = async (id, e) => {
  e.stopPropagation();
  try {
    const uid = Cookies.get("uid") || storage.getKey("id");
    if (!uid) return;
    const res = await axios.post(`${API}/api/notifications/clear`, {
      uid,
      notificationId: id
    });
    if (res.data && res.data.status) {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }
  } catch (err) {
    console.error(err);
  }
};

const handleClearAll = async () => {
  try {
    const uid = Cookies.get("uid") || storage.getKey("id");
    if (!uid) return;
    const res = await axios.post(`${API}/api/notifications/clear-all`, {
      uid
    });
    if (res.data && res.data.status) {
      setNotifications([]);
    }
  } catch (err) {
    console.error(err);
  }
};

  return (
    <>
      <header className="sticky top-0 z-40 h-[72px] !bg-[#0F1E25] border-b border-gray-800/80 flex items-center px-2 sm:px-3 lg:px-4 gap-1 sm:gap-2 select-none">
        
        {/* Left cluster: Hamburger & Capsule switcher */}
        <div className={`flex items-center gap-2 shrink-0 ${isCollapsed ? "lg:w-[64px]" : "lg:w-[244px]"}`}>
            {/* Mobile menu button - only on mobile */}
          <button 
            onClick={() => {
              if (isCollapsedMobile) {
                if (setIsCollapsedMobile) setIsCollapsedMobile(false);
                Event.emit("closeMobileSidebar");
              } else {
                if (setIsCollapsedMobile) setIsCollapsedMobile(true);
                Event.emit("openMobileSidebar");
              }
            }}
            className="lg:hidden w-10 h-10 grid place-items-center rounded-md hover:bg-accent/50 text-white focus:outline-none"
          >
            {isCollapsedMobile ? <X size={20} /> : <Menu size={20} />}
          </button>

          {/* Desktop sidebar collapse button - only on desktop */}
          <button 
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden lg:grid w-10 h-10 place-items-center rounded-md hover:bg-accent/50 text-gray-400 hover:text-white focus:outline-none cursor-pointer transition-all"
          >
            <Menu size={20} />
          </button>

          {/* Casino / Sports Switcher capsule inside Header */}
          {!isCollapsed && (
            <div className="hidden sm:flex items-center gap-2">
              <Link 
                to="/casino" 
                className={`relative overflow-hidden px-[15px] py-1.5 rounded-lg text-sm font-bold transition-colors inline-flex items-center justify-center ${
                  isCasinoActive ? "text-white" : "bg-sidebar text-[#8e969f] hover:text-white"
                }`}
                style={isCasinoActive ? { background: 'linear-gradient(135deg,#1fc16b 0%,#0f8a47 100%)' } : undefined}
              >
                <span className="relative z-10">Casino</span>
                <svg className="pointer-events-none absolute -bottom-2 -right-3 w-14 h-9 opacity-40" viewBox="0 0 100 60" fill="none" aria-hidden>
                  <path d="M0 50 Q 40 60 70 30 T 110 -10" stroke="white" strokeOpacity={isCasinoActive ? 0.35 : 0.18} strokeWidth="14" strokeLinecap="round" fill="none" />
                </svg>
              </Link>
              <Link 
                to="/sports" 
                className={`relative overflow-hidden px-[15px] py-1.5 rounded-lg text-sm font-bold transition-colors inline-flex items-center justify-center ${
                  isSportsActive ? "text-white" : "bg-sidebar text-[#8e969f] hover:text-white"
                }`}
                style={isSportsActive ? { background: 'linear-gradient(135deg,#ff7a1a 0%,#d94c0a 100%)' } : undefined}
              >
                <span className="relative z-10">Sports</span>
                <svg className="pointer-events-none absolute -bottom-2 -right-3 w-14 h-9 opacity-40" viewBox="0 0 100 60" fill="none" aria-hidden>
                  <path d="M0 50 Q 40 60 70 30 T 110 -10" stroke="white" strokeOpacity={isSportsActive ? 0.35 : 0.18} strokeWidth="14" strokeLinecap="round" fill="none" />
                </svg>
              </Link>
            </div>
          )}
        </div>

      <style>{`
        .header-logo-mobile {
          display: block !important;
          height: 32px;
          max-width: 40px;
          object-fit: contain;
        }
        .header-logo-desktop {
          display: none !important;
          height: 32px;
          max-width: 130px;
          object-fit: contain;
        }
        .header-chat-button {
          display: grid !important;
        }
        .header-search-button {
          display: none !important;
        }
        @media (min-width: 1024px) {
          .header-logo-mobile {
            display: none !important;
          }
          .header-logo-desktop {
            display: block !important;
          }
          .header-search-button {
            display: grid !important;
          }
        }
        .navbar-wallet button, 
        .navbar-wallet button:hover, 
        .navbar-wallet button:focus, 
        .navbar-wallet button:active {
          box-shadow: none !important;
          outline: none !important;
          --tw-ring-color: transparent !important;
          --tw-ring-shadow: none !important;
          --tw-shadow: none !important;
        }
      `}</style>

      {/* Logo */}
      <Link to="/" className="flex items-center">
        {/* Small Logo for Mobile */}
        <img 
          src={window?.siteConfig?.logo_small ? (API + window.siteConfig.logo_small) : "/assets/images/logo.png"} 
          alt="Logo" 
          className="header-logo-mobile"
          onError={(e) => {
            e.target.src = "/assets/images/logo.png";
          }}
        />
        {/* Big Logo for Tablet & Desktop */}
        <img 
          src={window?.siteConfig?.logo_big ? (API + window.siteConfig.logo_big) : "/assets/images/logo.png"} 
          alt="Logo" 
          className="header-logo-desktop"
          onError={(e) => {
            e.target.src = "/assets/images/logo.png";
          }}
        />
      </Link>

        {/* Spacing element to push content right */}
        <div className="flex-1" />

        {/* Right Section: Actions, Wallet, Profile */}
        <div className="flex items-center gap-2">
          
          {/* APK App Download */}
          {siteConfig?.show_app_download && siteConfig?.app_download_url && (
            <a 
              href={siteConfig.app_download_url.startsWith('http') ? siteConfig.app_download_url : `${API}/download-app`}
              target="_blank"
              rel="noopener noreferrer"
              title={`Download ${siteConfig.app_download_name || 'App'}`}
              className="hidden md:flex w-10 h-10 rounded-md items-center justify-center text-gray-400 hover:bg-accent/50 hover:text-white transition-all duration-150"
            >
              <Download size={18} />
            </a>
          )}

          {isAuthed ? (
            <div className="flex items-center gap-0.5 sm:gap-2">
              {/* 1. Wallet / Credit Balance */}
              <Credit />

              {/* 2. Search Trigger */}
              <button 
                onClick={() => setIsSearchModalOpen(true)}
                className="header-search-button w-10 h-10 place-items-center rounded-md text-gray-400 hover:bg-accent/50 hover:text-white focus:outline-none transition-all duration-150"
              >
                <Search size={18} />
              </button>

              {/* 3. User Profile Info & Dropdown */}
              <div className="flex items-center rounded-md">
                <User />
              </div>

              {/* 4. Notification System */}
              <div className="relative" ref={notifRef}>
                <button 
                  onClick={() => setIsNotifOpen(!isNotifOpen)}
                  className="w-10 h-10 grid place-items-center rounded-md text-gray-400 hover:bg-accent/50 hover:text-white focus:outline-none transition-all duration-150 relative"
                >
                  <Bell size={18} />
                  {notifications.length > 0 && (
                    <span 
                      className="absolute flex items-center justify-center rounded-full text-white font-extrabold animate-pulse"
                      style={{
                        top: "2px",
                        right: "2px",
                        background: "#ef4444",
                        fontSize: "9px",
                        width: "16px",
                        height: "16px",
                        border: "1.5px solid #0F1E25",
                        lineHeight: "1",
                      }}
                    >
                      {notifications.length}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown */}
                {isNotifOpen && (
                  <div 
                    className="absolute right-0 top-12 w-[320px] rounded-md shadow-2xl overflow-hidden z-50 flex flex-col max-h-[400px]"
                    style={{
                      background: "#1a2c38",
                      border: "1px solid #2a3a46",
                    }}
                  >
                    <div 
                      className="flex items-center justify-between px-4 py-3"
                      style={{
                        background: "#11212c",
                        borderBottom: "1px solid #2a3a46",
                      }}
                    >
                      <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-wider" style={{ color: "#fff" }}>
                        <Bell size={13} className="text-[#00e701]" />
                        <span>Notifications</span>
                      </div>
                      {notifications.length > 0 && (
                        <button 
                          onClick={handleClearAll} 
                          className="text-red-400 hover:text-red-500 text-[10px] font-bold uppercase tracking-wider"
                          style={{ background: "none", border: "none", cursor: "pointer" }}
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    
                    <div 
                      className="overflow-y-auto flex-1 custom-scrollbar"
                      style={{
                        background: "#1a2c38",
                      }}
                    >
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center text-xs italic" style={{ color: "#a0aeb5" }}>
                          No new notifications
                        </div>
                      ) : (
                        notifications.map(n => (
                          <div 
                            key={n.id} 
                            className="p-3.5 relative transition-all duration-150"
                            style={{
                              borderBottom: "1px solid #2a3a46",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#213743"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                          >
                            <h5 className="text-white text-xs font-bold pr-5 leading-normal mb-1">{n.title}</h5>
                            <p className="text-[11px] leading-relaxed mb-1.5" style={{ color: "#a0aeb5" }}>{n.content}</p>
                            <span className="text-[9px] font-semibold" style={{ color: "#7f8c8d" }}>{formatUserDateTime(n.date)}</span>
                            <button 
                              onClick={(e) => handleClearNotification(n.id, e)} 
                              className="absolute top-3.5 right-3.5 hover:text-gray-200 text-lg leading-none"
                              style={{
                                background: "none",
                                border: "none",
                                color: "#7f8c8d",
                                cursor: "pointer",
                              }}
                            >
                              &times;
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* 5. Live Chat Modal Trigger */}
              <button 
                onClick={onChatToggle}
                className="w-10 h-10 place-items-center rounded-md text-gray-400 hover:bg-accent/50 hover:text-white focus:outline-none transition-all duration-150 header-chat-button"
              >
                <MessageSquare size={18} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button 
                onClick={() => Event.emit('login_form')} 
                className="!px-6 !h-10 rounded-md text-white hover:!bg-[#2f4553] text-sm font-semibold !inline-flex !items-center !justify-center transition-colors !w-auto !min-w-max border-none bg-transparent cursor-pointer"
              >
                Login
              </button>
              <button 
                onClick={() => Event.emit('register_form')} 
                className="!px-6 !h-10 rounded-md !bg-[#1475e1] hover:!bg-[#1880f0] text-white text-sm font-semibold !inline-flex !items-center !justify-center transition-colors !w-auto !min-w-max border-none cursor-pointer"
              >
                Register
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Explore / Search Game Modal */}
      <div 
        className={"fixed inset-0 bg-black/80 flex items-start justify-center p-4 z-[9999] transition-all " + (isSearchModalOpen ? "opacity-100 visible" : "opacity-0 invisible")}
        onClick={() => setIsSearchModalOpen(false)}
      >
        <div 
          className="w-full max-w-lg bg-sidebar border border-gray-800 rounded-xl shadow-2xl p-4 outline-none my-20 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            onClick={() => setIsSearchModalOpen(false)}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-200 border-none bg-transparent cursor-pointer"
          >
            <X size={20} />
          </button>
          <div className="mt-4">
            {isSearchModalOpen && <ExploreModal onClose={() => setIsSearchModalOpen(false)} />}
          </div>
        </div>
      </div>

      {/* Chat Room Modal */}
      <div 
        className={"fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[9999] transition-all " + (isChatModalOpen ? "opacity-100 visible" : "opacity-0 invisible")}
        onClick={() => setIsChatModalOpen(false)}
      >
        <div 
          className="w-full max-w-md bg-sidebar border border-gray-800 rounded-xl shadow-2xl p-0 outline-none my-10 relative overflow-hidden h-[85vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 py-3 bg-slate-950/20 border-b border-gray-800/80">
            <h3 className="text-white font-bold text-sm uppercase tracking-wider">Chat Room</h3>
            <button 
              onClick={() => setIsChatModalOpen(false)}
              className="text-gray-500 hover:text-gray-200 border-none bg-transparent cursor-pointer"
            >
              <X size={20} />
            </button>
          </div>
          <div className="p-4 h-[calc(85vh-50px)] overflow-y-auto">
            {/* LiveChat can be placed here if needed */}
          </div>
        </div>
      </div>

      {/* Auth Modals portals */}
      <Login hideTrigger={true} />
      <Register hideTrigger={true} />
    </>
  );
}

export default Header;
